import { useState, useEffect } from "react";
import styled from "styled-components";
import { HelpCircle, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Container } from "../layout/Container";
import { getLegalPage } from "../../services/legalServices";
import { homeFaqs as fallbackFaqs } from "../../data/homeFaqs";

const Section = styled.section`
  padding: 2.5rem 0;
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.font.sectionTitle};
  font-weight: 700;
  color: hsl(${({ theme }) => theme.colors.foreground});
  margin: 0 0 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const List = styled.div`
  max-width: min(100%, 64rem);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Item = styled.div`
  background: hsl(${({ theme }) => theme.colors.card});
  border-radius: ${({ theme }) => theme.radius.xl};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  overflow: hidden;
`;

const QBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  text-align: left;
  font-size: clamp(0.9375rem, 0.88rem + 0.2vw, 1.0625rem);
  font-weight: 600;
  color: hsl(${({ theme }) => theme.colors.foreground});
`;

const Answer = styled.div`
  padding: 0 1.25rem 1rem;
`;

const AnswerText = styled.p`
  margin: 0;
  font-size: clamp(0.9375rem, 0.88rem + 0.2vw, 1.0625rem);
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

export default function HomeFaqSection() {
  const { i18n, t } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);
  const [items, setItems] = useState(fallbackFaqs);

  useEffect(() => {
    let cancel = false;
    getLegalPage("faq", i18n.language)
      .then((page) => {
        if (cancel || !page?.faqs?.length) return;
        const fromDb = page.faqs
          .filter((f) => f.question && f.answer)
          .slice(0, 6)
          .map((f) => ({
            q: f.question,
            a: String(f.answer).replace(/<[^>]+>/g, " ").trim(),
          }));
        if (fromDb.length) setItems(fromDb);
      })
      .catch(() => {});
    return () => {
      cancel = true;
    };
  }, [i18n.language]);

  return (
    <Section>
      <Container>
        <Title>
          <HelpCircle size={24} strokeWidth={2} color="hsl(221 67% 44%)" />
          {t("staticPages.faq.title", "Frequently Asked Questions")}
        </Title>
        <List>
          {items.map((faq, i) => (
            <Item key={faq.q}>
              <QBtn type="button" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                <span>{faq.q}</span>
                <ChevronRight
                  size={16}
                  strokeWidth={2}
                  style={{
                    flexShrink: 0,
                    color: "hsl(220 10% 46%)",
                    transition: "transform 0.2s",
                    transform: openFaq === i ? "rotate(90deg)" : "none",
                  }}
                />
              </QBtn>
              {openFaq === i && (
                <Answer>
                  <AnswerText>{faq.a}</AnswerText>
                </Answer>
              )}
            </Item>
          ))}
        </List>
      </Container>
    </Section>
  );
}
